import { useState } from 'react';
import { Calculator, Wallet } from 'lucide-react';
import {
  Iskelet,
  Kart,
  KartBaslik,
  KartIcerik,
  Tablo,
  TabloBaslik,
  TabloBaslikHucresi,
  TabloGovde,
  TabloHucre,
  TabloSatir,
  TarihAraligiSecici,
  tarihIso,
  type TarihAraligi,
} from '@emanetx/ui';
import { useHakedis } from './api.js';

const PARA_SEMBOL: Record<string, string> = { TRY: '₺', EUR: '€', USD: '$' };

function gecenAy(): TarihAraligi {
  const e = new Date();
  e.setDate(0); // ayın son günü (bir önceki ay)
  const s = new Date(e.getFullYear(), e.getMonth(), 1);
  return { baslangic: s, bitis: e };
}

export function SayfaHakedis() {
  const [aralik, setAralik] = useState<TarihAraligi>(gecenAy);

  const sorgu =
    aralik.baslangic && aralik.bitis
      ? { baslangic: tarihIso(aralik.baslangic)!, bitis: tarihIso(aralik.bitis)! }
      : null;

  const rapor = useHakedis(sorgu);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Hakediş</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Ortakların belirli dönemde noktalarından kazandığı komisyon.
          </p>
        </div>
        <TarihAraligiSecici deger={aralik} onDegisim={setAralik} />
      </header>

      {rapor.isLoading ? (
        <Iskelet className="h-48" />
      ) : !rapor.data ? null : rapor.data.ortaklar.length === 0 ? (
        <Kart>
          <KartIcerik className="text-fume-500 py-12 text-center text-sm">
            Bu aralıkta hakediş verisi yok.
          </KartIcerik>
        </Kart>
      ) : (
        <div className="space-y-4">
          {rapor.data.ortaklar.map((o) => (
            <Kart key={o.ortakId}>
              <KartBaslik>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="flex items-center gap-2 text-base font-semibold">
                    <Wallet size={16} className="text-marka-500" /> {o.ortakAd}
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="text-fume-500 text-xs">
                      Default komisyon: <span className="font-mono">%{o.komisyonYuzde}</span>
                    </span>
                    {Number(o.toplamHakedisTRY) > 0 && (
                      <span className="font-mono text-lg font-bold text-emerald-700 dark:text-emerald-400">
                        ₺
                        {Number(o.toplamHakedisTRY).toLocaleString('tr-TR', {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </KartBaslik>
              <KartIcerik>
                {o.satirlar.length === 0 ? (
                  <p className="text-fume-500 py-4 text-center text-sm">
                    Bu dönemde gelir oluşmadı.
                  </p>
                ) : (
                  <Tablo>
                    <TabloBaslik>
                      <tr>
                        <TabloBaslikHucresi>Nokta</TabloBaslikHucresi>
                        <TabloBaslikHucresi>Kiralama</TabloBaslikHucresi>
                        <TabloBaslikHucresi>Brüt Gelir</TabloBaslikHucresi>
                        <TabloBaslikHucresi>Komisyon</TabloBaslikHucresi>
                        <TabloBaslikHucresi>Hakediş</TabloBaslikHucresi>
                      </tr>
                    </TabloBaslik>
                    <TabloGovde>
                      {o.satirlar.map((s, i) => (
                        <TabloSatir key={`${s.noktaId}-${s.paraBirimi}-${i}`}>
                          <TabloHucre>
                            <span className="text-marka-700 dark:text-marka-400 font-mono text-xs">
                              {s.noktaKod}
                            </span>
                            <span className="ml-2 text-sm">{s.noktaAd}</span>
                          </TabloHucre>
                          <TabloHucre>
                            <span className="font-mono text-xs">{s.kiralamaSayisi}</span>
                          </TabloHucre>
                          <TabloHucre>
                            <span className="font-mono text-sm">
                              {PARA_SEMBOL[s.paraBirimi]}
                              {Number(s.brutGelir).toLocaleString('tr-TR', {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </TabloHucre>
                          <TabloHucre>
                            <span className="text-fume-500 font-mono text-xs">
                              %{s.komisyonYuzde}
                            </span>
                          </TabloHucre>
                          <TabloHucre>
                            <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                              {PARA_SEMBOL[s.paraBirimi]}
                              {Number(s.hakedis).toLocaleString('tr-TR', {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </TabloHucre>
                        </TabloSatir>
                      ))}
                    </TabloGovde>
                  </Tablo>
                )}
              </KartIcerik>
            </Kart>
          ))}
        </div>
      )}

      <p className="text-fume-400 text-xs">
        <Calculator size={11} className="inline" /> Hakediş = brüt gelir × komisyon%. Nokta bazında
        komisyon override edilebilir.
      </p>
    </div>
  );
}
